import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormControl, Grid, IconButton, Select, MenuItem } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import moment from "moment-timezone"
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import logger from "../../utils/logger";
import { useTimezone } from "../../hooks/useTimezone";

// ✅ FUNCIONES DE VALIDACIÓN DE FECHA/HORA (CON TIMEZONE)
const validateScheduleDateTime = (sendAt, timezone = "America/Lima") => {
	if (!sendAt) {
		return { valid: false, message: "Obligatorio" };
	}

	// ✅ Usar timezone de la empresa
	const now = moment.tz(timezone);
	let selectedTime;
	
	if (sendAt.includes('T')) {
		// Formato datetime-local: "YYYY-MM-DDTHH:mm"
		const dateTimeString = sendAt.replace('T', ' ');
		selectedTime = moment.tz(dateTimeString, 'YYYY-MM-DD HH:mm', timezone);
	} else {
		selectedTime = moment.tz(sendAt, timezone);
	}

	// ✅ VALIDACIÓN 1: FECHA/HORA VÁLIDA
	if (!selectedTime.isValid()) {
		return { valid: false, message: "❌ Fecha/hora inválida" };
	}

	// ✅ VALIDACIÓN 2: FECHA/HORA PASADA (usando la misma zona horaria)
	if (selectedTime.isBefore(now, 'minute')) {
		return { 
			valid: false, 
			message: `❌ No puedes agendar para una fecha/hora pasada. Hora actual: ${now.format('DD/MM/YYYY HH:mm')}` 
		};
	}

	// ✅ VALIDACIÓN 3: TIEMPO MÍNIMO (10 minutos)
	const timeDiff = selectedTime.diff(now, 'minutes');
	if (timeDiff < 10) {
		const minTime = now.clone().add(10, 'minutes');
		return { 
			valid: false, 
			message: `❌ El tiempo mínimo para agendar es 10 minutos. Hora más temprana permitida: ${minTime.format('DD/MM/YYYY HH:mm')}` 
		};
	}

	// ✅ VALIDACIÓN 4: TIEMPO MÁXIMO (1 año)
	if (timeDiff > 525600) { // 365 días * 24 horas * 60 minutos
		return { 
			valid: false, 
			message: "❌ No puedes agendar para más de 1 año en el futuro" 
		};
	}

	// ✅ VALIDACIÓN 5: FECHA INVÁLIDA (30 de febrero, 31 de abril, etc.)
	const day = selectedTime.date();
	const month = selectedTime.month() + 1;
	const year = selectedTime.year();
	
	// Verificar fechas inválidas específicas
	if (month === 2 && day > 29) {
		return { valid: false, message: "❌ Febrero no puede tener más de 29 días" };
	}
	if (month === 2 && day === 29 && !moment([year]).isLeapYear()) {
		return { valid: false, message: "❌ 29 de febrero solo existe en años bisiestos" };
	}
	if ([4, 6, 9, 11].includes(month) && day > 30) {
		return { valid: false, message: `❌ El mes ${month} no puede tener más de 30 días` };
	}

	return { valid: true, message: "" };
};

// ✅ FUNCIÓN DE VALIDACIÓN DE MENSAJE (CON LÍMITE DE 500 CARACTERES)
const validateMessage = (body) => {
	if (!body || body.trim().length === 0) {
		return { valid: false, message: "Obligatorio" };
	}
	
	if (body.length > 500) {
		return { valid: false, message: "❌ El mensaje no puede exceder 500 caracteres" };
	}
	
	if (body.trim().length < 3) {
		return { valid: false, message: "❌ El mensaje debe tener al menos 3 caracteres" };
	}
	
	// Verificar si solo tiene espacios o caracteres especiales
	const cleanMessage = body.trim().replace(/[^\w\s]/g, '').replace(/\s+/g, '');
	if (cleanMessage.length < 2) {
		return { valid: false, message: "❌ El mensaje debe contener al menos 2 caracteres válidos" };
	}
	
	return { valid: true, message: "" };
};

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

const ScheduleSchema = Yup.object().shape({
	body: Yup.string()
		.min(5, "Mensaje muy corto")
		.required("Obligatorio"),
	contactId: Yup.number().nullable().required("Obligatorio"),
	whatsappId: Yup.number().nullable().required("Obligatorio"),
	sendAt: Yup.string().required("Obligatorio")
});

const ScheduleModal = ({ open, onClose, scheduleId, contactId, cleanContact, reload, ticketId }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const { getConfiguredTimezone } = useTimezone();
	const timezone = getConfiguredTimezone();

	// ✅ Hora por defecto: hora actual + 20 minutos (en la zona horaria de la empresa)
	const getDefaultDateTime = () => {
		const now = moment.tz(timezone);
		return now.add(20, 'minutes').format('YYYY-MM-DDTHH:mm');
	};

	const initialState = {
		body: "",
		contactId: null,
		whatsappId: null,
		sendAt: getDefaultDateTime(),
		sentAt: ""
	};

	const [schedule, setSchedule] = useState(initialState);
	const [currentContact, setCurrentContact] = useState(null);
	const [contacts, setContacts] = useState([]);
	const [whatsapps, setWhatsapps] = useState([]);
	const [currentWhatsapp, setCurrentWhatsapp] = useState(null);
	const [attachment, setAttachment] = useState(null);
	const attachmentFile = useRef(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
	const messageInputRef = useRef();
	const [searchParam, setSearchParam] = useState("");
	const [autocompleteOpen, setAutocompleteOpen] = useState(false);
	const [loadingContacts, setLoadingContacts] = useState(false);
	
	// ✅ ESTADO PARA MODAL DE CONFIRMACIÓN DE AGENDAMIENTO
	const [scheduleConfirmationOpen, setScheduleConfirmationOpen] = useState(false);
	const [scheduleToConfirm, setScheduleToConfirm] = useState(null);
	
	// ✅ ESTADO PARA MODAL DE ERROR
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	
	// ✅ REF PARA VERIFICAR SI EL COMPONENTE ESTÁ MONTADO
	const isMounted = useRef(true);

	useEffect(() => {
		// ✅ Inicializar isMounted al montar
		isMounted.current = true;
		
		// ✅ Cleanup: Marcar como desmontado
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (contactId && contacts.length) {
			const contact = contacts.find(c => c.id === contactId);
			if (contact && isMounted.current) {
				setCurrentContact(contact);
			}
		}
	}, [contactId, contacts]);

	useEffect(() => {
		const { companyId } = user;
		if (open) {
			try {
				(async () => {
					// Cargar contactos
					const { data: contactList } = await api.get('/contacts/list', { params: { companyId: companyId } });
					let customList = contactList.map((c) => ({ id: c.id, name: c.name }));
					if (isArray(customList) && isMounted.current) {
						setContacts(customList);
					}
					
					// Cargar conexiones WhatsApp
					const { data: whatsappList } = await api.get('/whatsapp', { params: { companyId: companyId } });
					if (isArray(whatsappList) && isMounted.current) {
						setWhatsapps(whatsappList);
					}
					
					if (contactId && isMounted.current) {
						setSchedule(prevState => {
							return { ...prevState, contactId }
						});
					}
					
					// Si hay ticketId, obtener el whatsappId del ticket
					if (ticketId) {
						try {
							const { data: ticket } = await api.get(`/tickets/${ticketId}`);
							if (ticket && ticket.whatsappId && isMounted.current) {
								setSchedule(prevState => {
									return { ...prevState, whatsappId: ticket.whatsappId }
								});
								// ✅ CORREGIR: Usar whatsappList en lugar de whatsapps
								const whatsapp = whatsappList.find(w => w.id === ticket.whatsappId);
								if (whatsapp && isMounted.current) {
									setCurrentWhatsapp(whatsapp);
								} else {
									// No se encontró conexión del ticket
								}
								logger.dashboard.debug(`[ScheduleModal] Usando WhatsApp del ticket: ${ticket.whatsappId}`);
							}
						} catch (err) {
							logger.dashboard.error(`[ScheduleModal] Error obteniendo ticket: ${err.message}`);
						}
					}

					if (!scheduleId) return;

					const { data } = await api.get(`/schedules/${scheduleId}`);
					if (isMounted.current) {
						setSchedule(prevState => {
							return { ...prevState, ...data, sendAt: moment(data.sendAt).format('YYYY-MM-DDTHH:mm') };
						});
						if (isMounted.current) {
							setCurrentContact(data.contact);
						}
					}
					
					// ✅ CORREGIR: Establecer la conexión WhatsApp DESPUÉS de cargar whatsapps
					if (data.whatsappId && whatsappList.length > 0 && isMounted.current) {
						const whatsapp = whatsappList.find(w => w.id === data.whatsappId);
						if (whatsapp && isMounted.current) {
							setCurrentWhatsapp(whatsapp);
						} else {
							// No se encontró conexión con ID
						}
					} else {
						// No hay whatsappId o whatsapps vacío
						if (whatsappList.length > 0 && isMounted.current) {
							const defaultWhatsapp = whatsappList[0];
							if (isMounted.current) {
								setCurrentWhatsapp(defaultWhatsapp);
								setSchedule(prevState => ({
									...prevState,
									whatsappId: defaultWhatsapp.id
								}));
							}
						}
					}
				})()
			} catch (err) {
				if (isMounted.current) {
					toastError(err);
				}
			}
		}
	}, [open, scheduleId, contactId, ticketId, user]); // ✅ SOLO DEPENDENCIAS ESENCIALES

	const handleClose = () => {
		onClose();
		setAttachment(null);
		if (isMounted.current) {
			setSchedule({ ...initialState, sendAt: getDefaultDateTime() });
			// ✅ LIMPIAR ESTADOS DE CONFIRMACIÓN Y ERRORES
			setScheduleConfirmationOpen(false);
			setScheduleToConfirm(null);
			setErrorModalOpen(false);
			setErrorMessage("");
			setSearchParam("");
			setAutocompleteOpen(false);
		}
	};

	const handleAttachmentFile = (e) => {
		const file = head(e.target.files);
		if (file) {
			setAttachment(file);
		}
	};

	const handleSaveSchedule = async (values) => {
		try {
			// ✅ VALIDACIÓN 1: CAMPOS OBLIGATORIOS
			if (!values.contactId || !values.whatsappId || !values.body || !values.sendAt) {
				if (isMounted.current) {
					setErrorMessage("❌ Todos los campos son obligatorios: Contacto, Conexión, Mensaje y Fecha/Hora");
					setErrorModalOpen(true);
				}
				return;
			}

			// ✅ VALIDACIÓN 2: FECHA/HORA (con timezone)
			const dateTimeValidation = validateScheduleDateTime(values.sendAt, timezone);
			if (!dateTimeValidation.valid) {
				if (isMounted.current) {
					setErrorMessage(dateTimeValidation.message);
					setErrorModalOpen(true);
				}
				return;
			}

			// ✅ VALIDACIÓN 3: MENSAJE
			const messageValidation = validateMessage(values.body);
			if (!messageValidation.valid) {
				if (isMounted.current) {
					setErrorMessage(messageValidation.message);
					setErrorModalOpen(true);
				}
				return;
			}

			// ✅ VALIDACIÓN 4: CAMBIO MÍNIMO DE 30 MINUTOS (solo para UPDATE)
			if (scheduleId) {
				const originalSchedule = await api.get(`/schedules/${scheduleId}`);
				const originalTime = moment.tz(originalSchedule.data.sendAt, timezone);
				const newTime = moment.tz(values.sendAt.replace('T', ' '), 'YYYY-MM-DD HH:mm', timezone);
				const timeDiff = Math.abs(newTime.diff(originalTime, 'minutes'));
				
				if (timeDiff < 30) {
					if (isMounted.current) {
						setErrorMessage("❌ El cambio mínimo debe ser de 30 minutos. Hora original: " + 
							originalTime.format('DD/MM/YYYY HH:mm') + 
							" - Nueva hora: " + newTime.format('DD/MM/YYYY HH:mm'));
						setErrorModalOpen(true);
					}
					return;
				}
			}

			// ✅ MOSTRAR CONFIRMACIÓN ANTES DE GUARDAR
			const scheduleData = {
				contactId: values.contactId,
				whatsappId: values.whatsappId,
				body: values.body,
				sendAt: values.sendAt,
				sentAt: null
			};

			// ✅ PREPARAR DATOS PARA CONFIRMACIÓN
			const contact = contacts.find(c => c.id === values.contactId);
			const whatsapp = whatsapps.find(w => w.id === values.whatsappId);
			const formattedDateTime = moment.tz(values.sendAt.replace('T', ' '), 'YYYY-MM-DD HH:mm', timezone).format('DD/MM/YYYY HH:mm');

			setScheduleToConfirm({
				...scheduleData,
				contactName: contact ? contact.name : 'Contacto no encontrado',
				whatsappName: whatsapp ? whatsapp.name : 'Conexión no encontrada',
				formattedDateTime: formattedDateTime
			});
			setScheduleConfirmationOpen(true);

		} catch (err) {
			if (isMounted.current) {
				setErrorMessage(err.response?.data?.message || err.message || "Error al validar el agendamiento");
				setErrorModalOpen(true);
			}
		}
	};

	// ✅ FUNCIÓN PARA GUARDAR DESPUÉS DE CONFIRMAR
	const handleConfirmSchedule = async () => {
		try {
			if (!scheduleToConfirm) {
				toast.error("❌ Error: No hay datos para guardar");
				return;
			}

			const { contactName, whatsappName, formattedDateTime, ...scheduleData } = scheduleToConfirm;

			if (scheduleId) {
				await api.put(`/schedules/${scheduleId}`, scheduleData);
				toast.success("✅ Agendamiento actualizado correctamente");
			} else {
				await api.post("/schedules", scheduleData);
				toast.success("✅ Agendamiento creado correctamente");
			}
			
			// ✅ CERRAR MODAL Y LIMPIAR
			setScheduleConfirmationOpen(false);
			setScheduleToConfirm(null);
			
			// ✅ DELAY PARA QUE EL USUARIO VEA LA NOTIFICACIÓN
			setTimeout(() => {
				onClose();
				if (typeof reload === 'function') {
					reload();
				} else {
					window.location.reload();
				}
			}, 1000);
		} catch (err) {
			toastError(err);
		}
	};


	const deleteMedia = async () => {
		if (attachment) {
			setAttachment(null);
			attachmentFile.current.value = null;
		}

		if (schedule.mediaPath) {
			await api.delete(`/schedules/${schedule.id}/media-upload`);
			setSchedule((prev) => ({
				...prev,
				mediaPath: null,
			}));
			toast.success(i18n.t("scheduleModal.toasts.deleted"));
			if (typeof reload == "function") {
				logger.dashboard.debug("Reload function:", reload);
				logger.dashboard.debug("Executing reload");
				reload();
			}
		}
	};

	const handleCancelReminder = async () => {
		try {
			await api.post(`/schedules/${scheduleId}/cancel-reminder`);
			toast.success("Reunión cancelada exitosamente");
			if (typeof reload == 'function') {
				reload();
			}
			// Forzar actualización de página para eliminar residuos
			window.location.reload();
		} catch (err) {
			toastError(err);
		}
		setCancelConfirmationOpen(false);
	};

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={deleteMedia}
			>
				{i18n.t("scheduleModal.confirmationModal.deleteMessage")}
			</ConfirmationModal>
			<ConfirmationModal
				title="Cancelar Reunión"
				open={cancelConfirmationOpen}
				onClose={() => setCancelConfirmationOpen(false)}
				onConfirm={handleCancelReminder}
			>
				¿Está seguro que desea cancelar esta reunión? Se enviará un mensaje de cancelación al contacto.
			</ConfirmationModal>
			
			{/* ✅ MODAL DE CONFIRMACIÓN DE AGENDAMIENTO */}
			<ConfirmationModal
				title="¿ESTÁ SEGURO DE AGENDAR CITA?"
				open={scheduleConfirmationOpen}
				onClose={() => {
					setScheduleConfirmationOpen(false);
					setScheduleToConfirm(null);
				}}
				onConfirm={handleConfirmSchedule}
			>
				{scheduleToConfirm && (
					<div style={{ textAlign: 'left', lineHeight: '1.6' }}>
						<p><strong><span role="img" aria-label="calendar">📅</span> Fecha y Hora:</strong> {scheduleToConfirm.formattedDateTime}</p>
						<p><strong><span role="img" aria-label="person">👤</span> Contacto:</strong> {scheduleToConfirm.contactName}</p>
						<p><strong><span role="img" aria-label="mobile">📱</span> Conexión:</strong> {scheduleToConfirm.whatsappName}</p>
						<p><strong><span role="img" aria-label="message">💬</span> Mensaje:</strong></p>
						<div style={{ 
							background: '#f5f5f5', 
							padding: '10px', 
							borderRadius: '5px', 
							marginTop: '5px',
							maxHeight: '100px',
							overflowY: 'auto'
						}}>
							{scheduleToConfirm.body}
						</div>
						<p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
							<i>¿Confirma que desea agendar esta cita con los datos mostrados?</i>
						</p>
					</div>
				)}
			</ConfirmationModal>
			
			{/* ✅ MODAL DE ERROR */}
			<ConfirmationModal
				title="❌ Error de Validación"
				open={errorModalOpen}
				onClose={() => {
					setErrorModalOpen(false);
					setErrorMessage("");
				}}
				onConfirm={() => {
					setErrorModalOpen(false);
					setErrorMessage("");
				}}
				confirmText="Entendido"
				showCancel={false}
			>
				<div style={{ textAlign: 'left', lineHeight: '1.6', color: '#d32f2f' }}>
					<p>{errorMessage}</p>
				</div>
			</ConfirmationModal>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{schedule.status === 'ERRO' ? 'Error de Envío' : `Mensaje ${capitalize(schedule.status)}`}
				</DialogTitle>
				<div style={{ display: "none" }}>
					<input
						type="file"
						accept=".png,.jpg,.jpeg"
						ref={attachmentFile}
						onChange={(e) => handleAttachmentFile(e)}
					/>
				</div>
				<Formik
					initialValues={schedule}
					enableReinitialize={true}
					validationSchema={ScheduleSchema}
					onSubmit={(values, actions) => {
						handleSaveSchedule(values)
							.then(() => {
								actions.setSubmitting(false);
							})
							.catch((error) => {
								console.error("❌ [UPDATE TEST] ❌ Error en onSubmit:", error);
								actions.setSubmitting(false);
							});
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue, handleSubmit }) => {
						// ✅ VERIFICAR SI HAY ERRORES DE VALIDACIÓN (solo campos obligatorios)
						const hasValidationErrors = Boolean(
							!values.contactId || 
							!values.whatsappId || 
							!values.body || 
							!values.sendAt
						);
						
						return (
							<Form onSubmit={handleSubmit}>
							<DialogContent dividers style={{ padding: '20px' }}>
								{/* ✅ CAMPO CONTACTO - Mejorado con búsqueda similar a TransferTicket */}
								<Autocomplete
									style={{ width: '100%', marginBottom: 20 }}
									getOptionLabel={(option) => {
										if (!option || !option.name) return "";
										return option.name;
									}}
									value={currentContact || null}
									onChange={(e, contact) => {
										const contactId = contact ? Number(contact.id) : null;
										setFieldValue("contactId", contactId);
										setSchedule({ ...schedule, contactId });
										setCurrentContact(contact || null);
									}}
									options={contacts}
									getOptionSelected={(option, value) => {
										if (!option || !value) return false;
										return value.id === option.id;
									}}
									filterOptions={(options, { inputValue }) => {
										if (!inputValue || inputValue.trim() === "") {
											return options;
										}
										return options.filter(option => {
											if (!option || !option.name) return false;
											const searchTerm = inputValue.toLowerCase();
											return option.name.toLowerCase().includes(searchTerm);
										});
									}}
									freeSolo={false}
									autoHighlight
									noOptionsText="No se encontraron contactos"
									loading={loadingContacts}
									open={autocompleteOpen}
									onOpen={() => {
										if (isMounted.current) {
											setAutocompleteOpen(true);
										}
										if (contacts.length === 0) {
											if (isMounted.current) {
												setLoadingContacts(true);
											}
											api.get('/contacts/list', { params: { companyId: user.companyId } })
												.then(({ data: contactList }) => {
													if (isMounted.current) {
														let customList = contactList.map((c) => ({ id: c.id, name: c.name }));
														if (isArray(customList)) {
															setContacts(customList);
														}
														setLoadingContacts(false);
													}
												})
												.catch(() => {
													if (isMounted.current) {
														setLoadingContacts(false);
													}
												});
										}
									}}
									onClose={() => {
										if (isMounted.current) {
											setAutocompleteOpen(false);
										}
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Escribe para buscar contactos"
											variant="outlined"
											autoFocus
											onChange={(e) => {
												if (isMounted.current) {
													setSearchParam(e.target.value);
												}
											}}
											placeholder="Click aquí para ver contactos disponibles"
											error={touched.contactId && Boolean(errors.contactId)}
											helperText={touched.contactId ? errors.contactId : ""}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<React.Fragment>
														{loadingContacts ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</React.Fragment>
												),
											}}
										/>
									)}
									renderOption={(option) => (
										<div style={{ 
											display: 'flex', 
											flexDirection: 'column',
											padding: '8px 0'
										}}>
											<div style={{ fontWeight: 'bold' }}>
												{option.name}
											</div>
										</div>
									)}
								/>
								
								{/* ✅ CAMPO CONEXIÓN - Mejorado con Select similar a TransferTicket */}
								<FormControl variant="outlined" style={{ width: '100%', marginBottom: 20 }}>
									<Select
										fullWidth
										displayEmpty
										variant="outlined"
										value={values.whatsappId || ""}
										onChange={(e) => {
											const whatsappId = e.target.value ? Number(e.target.value) : null;
											setFieldValue("whatsappId", whatsappId);
											setSchedule({ ...schedule, whatsappId });
											const whatsapp = whatsapps.find(w => w.id === whatsappId);
											setCurrentWhatsapp(whatsapp || null);
										}}
										error={touched.whatsappId && Boolean(errors.whatsappId)}
										MenuProps={{
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											getContentAnchorEl: null,
										}}
										renderValue={() => {
											if (!values.whatsappId || values.whatsappId === "") {
												return (
													<div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
														<span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
														<span>Seleccione una conexión</span>
													</div>
												);
											}
											const whatsapp = whatsapps.find(w => w.id === values.whatsappId);
											if (!whatsapp) {
												return (
													<div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
														<span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
														<span>Seleccione una conexión</span>
													</div>
												);
											}
											return (
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
													<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
														<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
															<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
																{whatsapp.name || `Conexión ${whatsapp.id}`}
															</span>
															<span 
																style={{ 
																	fontSize: '10px', 
																	padding: '2px 6px', 
																	borderRadius: '10px',
																	backgroundColor: whatsapp.status === 'CONNECTED' ? '#25d366' : '#e1306c',
																	color: 'white',
																	fontWeight: 'bold'
																}}
															>
																{whatsapp.status || 'DISCONNECTED'}
															</span>
														</div>
														{whatsapp.waName && (
															<span style={{ fontSize: '12px', color: '#666' }}>
																{whatsapp.waName}
															</span>
														)}
													</div>
												</div>
											);
										}}
									>
										{whatsapps?.length > 0 &&
										whatsapps.map((whatsapp) => (
											<MenuItem dense key={whatsapp.id} value={whatsapp.id}>
												<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
													<span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
													<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
														<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
															<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
																{whatsapp.name || `Conexión ${whatsapp.id}`}
															</span>
															<span 
																style={{ 
																	fontSize: '10px', 
																	padding: '2px 6px', 
																	borderRadius: '10px',
																	backgroundColor: whatsapp.status === 'CONNECTED' ? '#25d366' : '#e1306c',
																	color: 'white',
																	fontWeight: 'bold'
																}}
															>
																{whatsapp.status || 'DISCONNECTED'}
															</span>
														</div>
														{whatsapp.waName && (
															<span style={{ fontSize: '12px', color: '#666' }}>
																{whatsapp.waName}
															</span>
														)}
													</div>
												</div>
											</MenuItem>
										))}
									</Select>
									{touched.whatsappId && errors.whatsappId && (
										<div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
											{errors.whatsappId}
										</div>
									)}
								</FormControl>
								
								{/* ✅ CAMPO MENSAJE - Con límite de 500 caracteres y contador */}
								<div style={{ marginBottom: 20 }}>
									<TextField
										rows={6}
										multiline={true}
										label="Mensaje"
										name="body"
										inputRef={messageInputRef}
										value={values.body || ""}
										onChange={(e) => {
											const value = e.target.value;
											if (value.length <= 500) {
												setFieldValue('body', value);
											}
										}}
										error={touched.body && Boolean(errors.body)}
										helperText={touched.body ? errors.body : ""}
										variant="outlined"
										margin="dense"
										fullWidth
										inputProps={{ maxLength: 500 }}
									/>
									<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
											<span style={{ fontSize: '0.75rem', color: '#666' }}>
												{(values.body || "").length}/500 caracteres
											</span>
										</div>
										{!attachment && !schedule.mediaPath && (
											<IconButton
												size="small"
												color="primary"
												onClick={() => attachmentFile.current.click()}
												disabled={isSubmitting}
												title="Adjuntar archivo"
											>
												<AttachFile />
											</IconButton>
										)}
									</div>
									{(schedule.mediaPath || attachment) && (
										<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
											<Button startIcon={<AttachFile />} size="small">
												{attachment ? attachment.name : schedule.mediaName}
											</Button>
											<IconButton
												size="small"
												onClick={() => setConfirmationOpen(true)}
												color="secondary"
											>
												<DeleteOutline />
											</IconButton>
										</div>
									)}
								</div>

								{/* ✅ CAMPO FECHA/HORA */}
								<TextField
									label="Fecha de Agendamiento"
									type="datetime-local"
									name="sendAt"
									value={values.sendAt || ""}
									onChange={(e) => setFieldValue('sendAt', e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
									error={touched.sendAt && Boolean(errors.sendAt)}
									helperText={touched.sendAt ? errors.sendAt : ""}
									variant="outlined"
									fullWidth
									margin="dense"
								/>
							</DialogContent>
							<DialogActions>
								{scheduleId && schedule.isReminderSystem && (
									<Button
										onClick={() => setCancelConfirmationOpen(true)}
										color="secondary"
										disabled={isSubmitting}
										variant="outlined"
									>
										CANCELAR
									</Button>
								)}
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									SALIR
								</Button>
								{scheduleId ? (
									<Button
										type="submit"
										color="primary"
										disabled={isSubmitting || hasValidationErrors}
										variant="contained"
										className={classes.btnWrapper}
									>
										{i18n.t("scheduleModal.buttons.okEdit")}
										{isSubmitting && (
											<CircularProgress
												size={24}
												className={classes.buttonProgress}
											/>
										)}
									</Button>
								) : (
									<Button
										type="submit"
										color="primary"
										disabled={isSubmitting || hasValidationErrors}
										variant="contained"
										className={classes.btnWrapper}
									>
										{i18n.t("scheduleModal.buttons.okAdd")}
										{isSubmitting && (
											<CircularProgress
												size={24}
												className={classes.buttonProgress}
											/>
										)}
									</Button>
								)}
							</DialogActions>
						</Form>
					);
				}}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ScheduleModal;